import axios, { AxiosError } from 'axios';
import { logout, catchError } from '../redux/reducers';
import { isLoggedInFromFlag } from '../utils/authCookies';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

const api = axios.create({
  baseURL: process.env.REACT_APP_API,
  // Send the httpOnly auth cookies the API sets; the API authenticates via them.
  withCredentials: true,
});

const UNAUTHORIZED = 401;
const FORBIDDEN = 403;
const CONFLICT = 409;
const SERVER_ERROR = 500;
const interceptorExceptions = [
  '/pending_change_request',
  '/assignable_properties',
  '/token',
  '/auth/logout',
  '/moderations',
];

export const setApiInterceptors = (store: any) => {
  // Response Interceptor

  // Function that will be called to refresh authorization on a 401.
  //
  // The refresh token is an httpOnly cookie the browser sends automatically
  // (withCredentials). The refresh endpoint sets a fresh access cookie via
  // Set-Cookie on success — there is nothing to read or store client-side. We
  // gate on the readable session flag to avoid a pointless refresh call when no
  // session exists.
  const refreshAuthLogic = (failedRequest: any): Promise<any> => {
    if (!isLoggedInFromFlag()) {
      return Promise.resolve();
    }
    return api.post(`/v1/auth/token`, {}).then(() => {
      // Drop any stale Authorization header so the retry authenticates via the
      // freshly-set access cookie.
      if (failedRequest?.response?.config?.headers) {
        delete failedRequest.response.config.headers['Authorization'];
      }
      return Promise.resolve();
    });
  };

  createAuthRefreshInterceptor(api, refreshAuthLogic);

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async function (error: AxiosError) {
      if (error?.config?.url) {
        //check for exception endpoints (interceptorExceptions array) first, for cases where we do not want to respond to an error
        if (
          interceptorExceptions.some(
            (exception) => error?.config?.url && error?.config?.url.includes(exception),
          )
        ) {
          return;
        }
        // csv import errors are rendered on page instead of in snackbar, and are handled as a special case
        if (error.config.url.includes('data_import')) {
          return Promise.reject(error.response?.data?.errors);
        }
        if (
          error.config.url.includes('/v1/admin/communities/') &&
          error.config.method === 'get'
        ) {
          const pathCount = error.config.url.split('/').length - 1;
          // check if call returns 500 for a get/community req which would render an empty community form, redirect to server error page instead
          if (
            pathCount === 4 &&
            error?.response?.status &&
            error?.response?.status >= SERVER_ERROR
          ) {
            return store.dispatch(catchError({ ...error.response, redirect: 500 }));
            //show 403/Forbidden page if user attempts to view a community they do not have permission to edit
          } else if (
            pathCount === 4 &&
            error?.response?.status &&
            error?.response?.status === FORBIDDEN
          ) {
            return store.dispatch(catchError({ ...error.response, redirect: 403 }));
          }
        }
      }

      if (error?.response?.status === UNAUTHORIZED) {
        //logout user if 401 error is received from server
        console.log('Logging out user on 401 response from server');
        store.dispatch(logout(false, false));
      } else if (error?.response?.status === CONFLICT) {
        return Promise.reject({
          error: error.response?.status,
          results: error.response?.data?.results,
        });
      } else {
        //return error to render in snackbar/toast message
        store.dispatch(catchError(error.response));
        return Promise.reject(error.response?.data?.errors);
      }
    },
  );

  // Request Interceptor
  // Auth travels via the httpOnly access cookie (sent automatically because the
  // axios instance uses withCredentials). We no longer attach an Authorization
  // header; we only stamp the timezone the API expects.
  api.interceptors.request.use((config) => {
    config.headers['X-Timezone-Offset'] = new Date().getTimezoneOffset();
    return config;
  });
};

export default api;
